import {
  ArrowTrendingUpIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PhotoIcon,
  ShoppingBagIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline'

export const PRESET_CATEGORIES = [
  {
    name: 'Research',
    icon: MagnifyingGlassIcon,
    prompts: [
      {
        title: 'Analyze my competitors',
        prompt: 'Analyze my competitors and their ad strategies, make recommendations for us',
      },
      {
        title: 'Find top competitor ads',
        prompt:
          'Find top competitor ads and write a detailed report on which ads we should replicate for our product',
      },
      {
        title: 'Research top trends and create ads',
        prompt:
          'Research top trends about my product on X, Reddit, online and the ad library. Then create 10 highly performing ad concepts for me to launch instantly',
      },
      {
        title: "Explain my competitor's strategies",
        prompt:
          "Explain my competitor's strategies and write a report on how they market their products and what we can learn from it",
      },
    ],
  },
  {
    name: 'Writing',
    icon: PencilSquareIcon,
    prompts: [
      {
        title: 'Create 10 catchy hooks',
        prompt:
          'Create 10 catchy hooks for my product that stop the scroll and grab attention in the first 3 seconds. For each hook, provide a version for short-form video (TikTok/Reels) and a version for static ad copy, and briefly explain the psychological trigger each one uses',
      },
      {
        title: 'Write 5 scripts for AI UGC videos',
        prompt:
          'Write 5 scripts for AI UGC videos that feel authentic and relatable, each 30–60 seconds long. Structure each script with a strong scroll-stopping hook, a problem-solution narrative, and a clear call to action. Vary the tone and angle across all 5 so they target different customer personas or pain points',
      },
      {
        title: 'Create copy for our Meta ads',
        prompt:
          'Create copy for our Meta ads with primary text, headline, and description for 5 ad variations. Each variation should target a different customer pain point or desire, include a compelling hook, weave in social proof where relevant, and end with a conversion-focused call to action',
      },
    ],
  },
  {
    name: 'Trends',
    icon: ArrowTrendingUpIcon,
    prompts: [
      {
        title: 'Research Reddit for my product',
        prompt:
          'Research Reddit for my product by finding the most relevant subreddits and identifying what real people say about products like mine. Extract the top pain points, desires, objections, and the exact language customers use. Summarize the key insights and highlight phrases I can directly use in my ad copy',
      },
      {
        title: 'Find new ad formats I can launch',
        prompt:
          "Find new ad formats I can launch by researching what's currently performing best on Meta and TikTok in my niche. Identify emerging creative formats, underused placements, and gaps my competitors aren't exploiting. Return a prioritized list with a brief description and implementation tip for each format",
      },
      {
        title: 'Search the web for trends',
        prompt:
          'Search the web for trends relevant to my product and industry right now. Look for viral topics, cultural moments, and rising conversations I can tie my marketing to. Summarize the top 5–10 trends and for each one suggest a concrete way to use it in an ad campaign or content piece',
      },
    ],
  },
  {
    name: 'Short Video Ads',
    icon: VideoCameraIcon,
    prompts: [
      {
        title: 'Create Seedance videos',
        prompt: 'Create Seedance videos',
      },
      {
        title: 'Create 1 video in English, one in German, and one in Spanish',
        prompt: 'Create 1 video in English, one in German, and one in Spanish',
      },
      {
        title: 'Research Reddit and create videos',
        prompt:
          'Research Reddit to identify the most relevant subreddits for my product and analyze real user discussions about similar products. Extract key pain points, desires, objections, and exact customer language (quotes where possible). Summarize findings into a clear report, then create 3 videos, each showing the problem, the customer perspective, and how my product solves it.',
      },
      {
        title: 'Analyze my competitors and create videos',
        prompt:
          "Analyze my competitors' UGC ads and identify their key strategies. Summarize the findings in a clear report, then apply these insights to create 3 videos.",
      },
    ],
  },
  {
    name: 'Static Ads',
    icon: PhotoIcon,
    prompts: [
      {
        title: 'Create static ads for Meta',
        prompt:
          'Create static ads for Meta: Research what ad formats perform best in my niche, then create 5 static ad variations for my product using different proven formats - like social proof posts, product showcases, and before/after comparisons',
      },
      {
        title: 'Create 5 catchy statics for my product',
        prompt: 'Create 5 catchy statics for my product based on popular formats that you know',
      },
      {
        title: 'Create e-com asset for my product',
        prompt: 'Create e-com asset for my product, make it a mix of creative and proven ideas',
      },
      {
        title: 'Recreate this competitor ad',
        prompt:
          'Recreate this competitor ad by looking at the ad I attached to this chat and create 5 version of this ad for my product',
      },
    ],
  },
  {
    name: 'E-Com Assets',
    icon: ShoppingBagIcon,
    prompts: [
      {
        title: 'Product lifestyle shots',
        prompt:
          'Create 2 lifestyle shots of my product in a fitting scene that I can use for a store page or a social media post',
      },
      {
        title: 'Amazon A+ Content',
        prompt:
          'Create A+ content modules for my Amazon listing that highlight key benefits, answer common objections, and outshine competitors. Use comparison charts, lifestyle imagery, and benefit-driven copy that converts browsers into buyers.',
      },
      {
        title: 'Shopify and landing page assets',
        prompt:
          'Design high-converting visual assets for my Shopify collection pages and landing pages. Include hero images, trust badges, benefit callouts, and lifestyle banners that tell a cohesive brand story and reduce bounce rate.',
      },
      {
        title: 'Product infographics and inserts',
        prompt:
          'Create scroll-stopping product infographics for my Amazon gallery and Shopify PDPs. Break down features, ingredients, or use cases visually. Also generate a matching packaging insert design that drives reviews and repeat purchases.',
      },
    ],
  },
  {
    name: 'Analysis',
    icon: ChartBarIcon,
    prompts: [
      {
        title: 'Meta ad account audit',
        prompt:
          "Audit my Meta ad account and identify what's working and what's underperforming. Break down performance by campaign objective, creative format, and audience segment. Flag any wasted spend, audience overlap, or fatigue issues. Give me a prioritized list of quick wins I can act on this week to improve ROAS.",
      },
      {
        title: 'TikTok content performance analysis',
        prompt:
          "Analyze my TikTok account's recent content and identify which videos are outperforming and why. Break down hook retention, watch-through rates, and engagement patterns. Tell me which formats, topics, and posting times are driving the most growth. Give me a specific content formula I should double down on based on what the data shows.",
      },
      {
        title: 'Meta vs TikTok creative comparison',
        prompt:
          "Compare the performance of my ad creatives across Meta and TikTok. Identify which creative styles, hooks, and formats resonate on each platform and where there's a mismatch. Show me where I'm running the same creative on both when I shouldn't be. Recommend platform-specific creative adjustments I can make to stop leaving performance on the table.",
      },
      {
        title: 'Competitor account teardown',
        prompt:
          "Pick my top 3 competitors on Meta and TikTok and tear down their paid and organic strategy. Identify their highest-performing ad creatives, posting cadence, and content themes. Show me the gaps they're missing that I could own. Give me a concrete list of creative angles and formats I should test based on what's clearly working in my space.",
      },
    ],
  },
]
